// Analytics tab › Overview (spec §4): header row, "How it fits together"
// chain strip, and the inventory cards grid computed from REAL project data
// (connections, business areas, schemas, object catalog, pending requests).
import type { ReactNode } from 'react'
import { ArrowRight, Database, Sparkles } from 'lucide-react'
import type { AnalyticsInventory } from './AnalyticsTypes'
import type { AnalyticsSection } from './shared'
import { InlineError, TypeChip } from './shared'

function ChainChip({ chip, label, optional }: { chip: ReactNode; label: string; optional?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '5px 11px',
        borderRadius: 999,
        border: '1px solid var(--color-border)',
        background: 'var(--color-surface-2)',
        fontSize: 13.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {chip}
      {label}
      {optional && (
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--color-text-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 5,
            padding: '0 5px',
          }}
        >
          optional
        </span>
      )}
    </span>
  )
}

function InventoryCard({
  chip, title, count, unit, meta, onClick,
}: {
  chip: ReactNode
  title: string
  count: number | null
  unit: string
  meta: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left',
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 12,
        padding: '16px 18px',
        cursor: 'pointer',
        color: 'var(--color-text)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minWidth: 0,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-strong)' }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        {chip}
        <span style={{ fontWeight: 600, fontSize: 15 }}>{title}</span>
      </span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 29, fontWeight: 700, letterSpacing: '-1px' }}>
          {count === null ? '—' : count}
        </span>
        <span style={{ fontSize: 14.5, color: 'var(--color-text-secondary)' }}>{unit}</span>
      </span>
      <span
        style={{
          fontSize: 14.5,
          color: 'var(--color-text-secondary)',
          minHeight: 22,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {meta}
      </span>
    </button>
  )
}

export default function OverviewSection({
  inventory, loading, error, onReload, onNavigate, onStartBuild,
}: {
  inventory: AnalyticsInventory
  loading: boolean
  error: string | null
  onReload: () => void
  onNavigate: (section: AnalyticsSection, opts?: { visualsTab?: 'mine' | 'standardised' }) => void
  onStartBuild: () => void
}) {
  // Schemas (M) have no card of their own: every schema belongs to a business
  // area, which is the superset — the Business Areas card counts them instead
  // (the Schema Design rail section still edits them directly).
  const {
    connections, areas, schemas, visuals, dashboards, reports, processes, pages,
    foresight, standardize,
  } = inventory

  function names<T>(list: T[] | null, pick: (x: T) => string): string {
    return list && list.length > 0 ? list.slice(0, 3).map(pick).join(' · ') : ''
  }

  const myVisuals = visuals ? visuals.filter((v) => !v.in_navigation) : null
  const stdVisuals = visuals ? visuals.filter((v) => v.in_navigation) : null
  const pending = standardize ? standardize.requests.filter((r) => r.status === 'pending') : null
  // Health from the stored last-test stamp (no live probing on Overview load).
  const failingConnections = connections
    ? connections.filter(
        (c: { LAST_TEST?: { status?: string } }) => c.LAST_TEST?.status === 'failed')
    : []

  const chainArrow = (
    <ArrowRight size={13} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
  )

  return (
    <div style={{ padding: '28px 34px 60px', maxWidth: 1180, width: '100%', margin: '0 auto' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 320 }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: '-0.4px' }}>
            Analytics overview
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 15.5, lineHeight: 1.55, color: 'var(--color-text-secondary)', maxWidth: 640 }}>
            Everything that powers reporting in this project, in one place. Start with the guided
            flow — it connects a data source and builds a working analytics app for you.
          </p>
        </div>
        <button
          className="btn btn-primary"
          style={{ borderRadius: 10, padding: '11px 20px', fontSize: 15 }}
          onClick={onStartBuild}
        >
          <Sparkles size={15} />
          Build analytics
        </button>
      </div>

      {/* "How it fits together" chain strip */}
      <div
        style={{
          marginTop: 22,
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 12,
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)', marginRight: 4 }}>
          How it fits together
        </span>
        <ChainChip
          chip={<Database size={14} style={{ color: '#7d8590' }} />}
          label="Connection"
        />
        {chainArrow}
        <ChainChip chip={<TypeChip code="A" size={16} />} label="Business Area" />
        {chainArrow}
        <ChainChip chip={<TypeChip code="V" size={16} />} label="Visuals" />
        {chainArrow}
        <ChainChip chip={<TypeChip code="D" size={16} />} label="Dashboard" />
      </div>

      {error && (
        <div style={{ marginTop: 18 }}>
          <InlineError message={error} onRetry={onReload} />
        </div>
      )}

      {/* Inventory cards */}
      <div
        style={{
          marginTop: 18,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(268px, 1fr))',
          gap: 16,
        }}
      >
        {loading ? (
          [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div
              key={i}
              style={{
                height: 118,
                borderRadius: 12,
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                opacity: 0.55,
              }}
            />
          ))
        ) : (
          <>
            <InventoryCard
              chip={
                <span
                  style={{
                    width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
                  }}
                >
                  <Database size={14} style={{ color: '#7d8590' }} />
                </span>
              }
              title="Connections"
              count={connections ? connections.length : null}
              unit="sources"
              meta={
                connections === null
                  ? 'Could not load connections'
                  : connections.length === 0
                    ? 'Link an external database or warehouse'
                    : failingConnections.length > 0
                      ? `${failingConnections.length} failing test${failingConnections.length > 1 ? 's' : ''} — ${
                          failingConnections
                            .slice(0, 2)
                            .map((c: { CONNECTION_NAME?: string; CONNECTION_ID: string }) =>
                              c.CONNECTION_NAME || c.CONNECTION_ID)
                            .join(', ')}`
                      : names(connections, (c: { CONNECTION_NAME?: string; CONNECTION_ID: string }) =>
                          c.CONNECTION_NAME || c.CONNECTION_ID)
              }
              onClick={() => onNavigate('connections')}
            />
            <InventoryCard
              chip={<TypeChip code="A" size={26} />}
              title="Business Areas"
              count={areas ? areas.length : null}
              unit="topics"
              meta={
                areas === null
                  ? 'Could not load business areas'
                  : areas.length === 0
                    ? 'Topics the AI Assistant can answer on'
                    // The area is the superset of its schema, so the schema
                    // count rides along here instead of on a card of its own.
                    : names(areas, (a: { area_name: string }) => a.area_name)
                      + (schemas && schemas.length > 0
                        ? ` · ${schemas.length} schema design${schemas.length === 1 ? '' : 's'}`
                        : '')
              }
              onClick={() => onNavigate('areas')}
            />
            <InventoryCard
              chip={<TypeChip code="V" size={26} />}
              title="Visuals"
              count={visuals ? visuals.length : null}
              unit="charts & KPIs"
              meta={
                visuals === null
                  ? 'Could not load visuals'
                  : `${myVisuals?.length ?? 0} mine · ${stdVisuals?.length ?? 0} standardised`
              }
              onClick={() => onNavigate('visuals')}
            />
            <InventoryCard
              chip={<TypeChip code="D" size={26} />}
              title="Dashboards"
              count={dashboards ? dashboards.length : null}
              unit="dashboards"
              meta={
                dashboards === null
                  ? 'Could not load dashboards'
                  : dashboards.length === 0
                    ? 'Grids of visuals with shared filters'
                    : names(dashboards, (d: { title: string; obj_name: string }) => d.title || d.obj_name)
              }
              onClick={() => onNavigate('dashboards')}
            />
            <InventoryCard
              chip={<TypeChip code="R" size={26} />}
              title="Reports"
              count={reports ? reports.length : null}
              unit="formatted reports"
              meta={
                reports === null
                  ? 'Could not load reports'
                  : reports.length === 0
                    ? 'Pixel-perfect printable (Jasper) documents'
                    : names(reports, (r: { title: string; obj_name: string }) => r.title || r.obj_name)
              }
              onClick={() => onNavigate('reports')}
            />
            <InventoryCard
              chip={<TypeChip code="G" size={26} />}
              title="Processes"
              count={processes ? processes.length : null}
              unit="general processes"
              meta={
                processes === null
                  ? 'Could not load processes'
                  : processes.length === 0
                    ? 'Batch runs: source SQL → calculations → posted rows'
                    : names(processes, (p: { name: string; obj_name: string }) => p.name || p.obj_name)
              }
              onClick={() => onNavigate('processes')}
            />
            <InventoryCard
              chip={<TypeChip code="S" size={26} />}
              title="Smart Pages"
              count={pages ? pages.length : null}
              unit="pages"
              meta={
                pages === null
                  ? 'Could not load smart pages'
                  : pages.length === 0
                    ? 'Pages that embed visuals & dashboards'
                    : names(pages, (p: { title: string; obj_name: string }) => p.title || p.obj_name)
              }
              onClick={() => onNavigate('smartpages')}
            />
            <InventoryCard
              chip={<TypeChip code="F" size={26} />}
              title="Foresight"
              count={foresight ? foresight.length : null}
              unit="experiments"
              meta={
                foresight === null
                  ? 'Could not load foresight models'
                  : foresight.length === 0
                    ? 'Forecasting & prediction (ML) experiments'
                    : names(foresight, (f: { name: string; obj_name: string }) => f.name || f.obj_name)
              }
              onClick={() => onNavigate('foresight')}
            />
            {standardize !== null && (standardize.can_review || (pending?.length ?? 0) > 0) && (
              <InventoryCard
                chip={
                  <span
                    style={{
                      width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
                      color: 'var(--color-warning)',
                      fontWeight: 700,
                    }}
                  >
                    !
                  </span>
                }
                title="Pending requests"
                count={pending ? pending.length : 0}
                unit="to review"
                meta="End users asking to standardise visuals"
                onClick={() => onNavigate('visuals', { visualsTab: 'standardised' })}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
